import { useOnClickOutside } from '@/hooks/useOnClickOutSide';
import { useState, useRef, useCallback } from 'react';
import clsx from 'clsx';
import * as ViewModel from '@/core/presenter/viewModel';
import { useStores } from '@/stores';

interface IProps {
  isWritting: boolean;
  selectedAsset: ViewModel.IAsset | undefined;
  onChangeSelectedAssetValue: (assetType: ViewModel.AssetType) => void;
}

const SelectAssetsCombobox = ({ isWritting, selectedAsset, onChangeSelectedAssetValue }: IProps) => {
  const { userStore } = useStores();
  const [visible, setVisible] = useState<boolean>(false);
  const ref = useRef<HTMLDivElement>(null);
  useOnClickOutside(ref, () => setVisible(false));

  const handleAssetSelect = (assetType: ViewModel.AssetType) => {
    setVisible(false);
    onChangeSelectedAssetValue(assetType);
  };

  const getCurrentAssetInfo = useCallback(() => {
    if (!selectedAsset) {
      return {
        balance: userStore.myAsset?.tokenAsset?.balance ?? 0,
        name: userStore.myAsset?.tokenAsset?.name ?? '',
        symbol: userStore.myAsset?.tokenAsset?.symbol ?? '',
        icon: userStore.myAsset?.tokenAsset?.iconPath ?? '',
      };
    }

    // selectedAsset이 coinAsset인지 tokenAsset인지 비교
    const isCoinAsset = selectedAsset.symbol === userStore.myAsset?.coinAsset?.symbol;

    return isCoinAsset
      ? {
        balance: userStore.myAsset?.coinAsset?.balance,
        name: userStore.myAsset?.coinAsset?.name,
        symbol: userStore.myAsset?.coinAsset?.symbol,
        icon: userStore.myAsset?.coinAsset?.iconPath,
      }
      : {
        balance: userStore.myAsset?.tokenAsset?.balance,
        name: userStore.myAsset?.tokenAsset?.name,
        symbol: userStore.myAsset?.tokenAsset?.symbol,
        icon: userStore.myAsset?.tokenAsset?.iconPath,
      };
  }, [selectedAsset, userStore.myAsset]);

  const currentAsset = getCurrentAssetInfo();

  const isTokenSelected = selectedAsset?.symbol === userStore.myAsset?.tokenAsset?.symbol;
  const isCoinSelected = selectedAsset?.symbol === userStore.myAsset?.coinAsset?.symbol;

  return (
    <div
      className={clsx('send-asset-group send-filled-group', !isWritting && 'hidden')}
      style={{ display: isWritting ? 'flex' : 'none' }}
      ref={ref}
    >
      <label className="send-asset-label text-base font-medium">자산:</label>
      <div className="send-asset-select-wrapper">
        <div className="send-asset-select" onClick={() => setVisible(!visible)}>
          <div className="asset-select-display">
            <img src={currentAsset.icon} alt={currentAsset.name} className="asset-icon" />
            <div className="asset-info">
              <span className="asset-name text-base font-semibold">{currentAsset.name}</span>
              <span className="asset-balance text-sm font-normal">
                잔액: {currentAsset.balance} {currentAsset.symbol}
              </span>
            </div>
          </div>
          <button type="button" className="asset-select-arrow">
            <img src="./images/arrow-down.png" alt="자산 선택" className="asset-select-arrow-icon" />
          </button>
        </div>

        {/* 드롭다운 리스트 */}
        <div className={clsx('asset-dropdown', !visible && 'hidden')}>
          <div
            className={clsx('asset-dropdown-item', isTokenSelected && 'active')}
            onClick={() => handleAssetSelect(ViewModel.AssetType.TOKEN)}
          >
            <img
              src={userStore.myAsset?.tokenAsset?.iconPath}
              alt={userStore.myAsset?.tokenAsset?.name}
              className="asset-dropdown-icon"
            />
            <div className="asset-dropdown-info">
              <span className="asset-dropdown-name text-base font-semibold">
                {userStore.myAsset?.tokenAsset?.name}
              </span>
              <span className="asset-dropdown-balance text-sm font-normal">
                잔액: {userStore.myAsset?.tokenAsset?.balance} {userStore.myAsset?.tokenAsset?.symbol}
              </span>
            </div>
          </div>

          <div
            className={clsx('asset-dropdown-item', isCoinSelected && 'active')}
            onClick={() => handleAssetSelect(ViewModel.AssetType.COIN)}
          >
            <img
              src={userStore.myAsset?.coinAsset?.iconPath}
              alt={userStore.myAsset?.coinAsset?.name}
              className="asset-dropdown-icon"
            />
            <div className="asset-dropdown-info">
              <span className="asset-dropdown-name text-base font-semibold">
                {userStore.myAsset?.coinAsset?.name}
              </span>
              <span className="asset-dropdown-balance text-sm font-normal">
                잔액: {userStore.myAsset?.coinAsset?.balance} {userStore.myAsset?.coinAsset?.symbol}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectAssetsCombobox;
